import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';
import {
  Briefcase,
  FileText,
  Users,
  Mail,
  TrendingUp,
  Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState({
    services: 0,
    caseStudies: 0,
    articles: 0,
    contacts: 0,
    unreadContacts: 0
  });
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [services, caseStudies, articles, contacts] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/case-studies`),
        axios.get(`${API}/admin/articles`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers })
      ]);

      setStats({
        services: services.data.length,
        caseStudies: caseStudies.data.length,
        articles: articles.data.length,
        contacts: contacts.data.length,
        unreadContacts: contacts.data.filter(c => !c.read).length
      });
      setRecentContacts(contacts.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: Briefcase, label: 'Services', value: stats.services, color: 'from-blue-500 to-blue-600' },
    { icon: FileText, label: 'Case Studies', value: stats.caseStudies, color: 'from-green-500 to-green-600' },
    { icon: FileText, label: 'Blog Posts', value: stats.articles, color: 'from-purple-500 to-purple-600' },
    { icon: Mail, label: 'Contact Messages', value: stats.contacts, badge: stats.unreadContacts, color: 'from-[#ff6b35] to-[#ff8f5c]' },
  ];

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Welcome back! Here's what's happening.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                {stat.badge > 0 && (
                  <span className="px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
                    {stat.badge} new
                  </span>
                )}
              </div>
              <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-gray-500 text-sm mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Recent Contacts */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Recent Contact Messages</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {recentContacts.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No contact messages yet
              </div>
            ) : (
              recentContacts.map((contact) => (
                <div key={contact.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        contact.read ? 'bg-gray-100' : 'bg-[#ff6b35]/10'
                      }`}>
                        <span className={`font-semibold ${
                          contact.read ? 'text-gray-500' : 'text-[#ff6b35]'
                        }`}>
                          {contact.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{contact.name}</span>
                          {!contact.read && (
                            <span className="w-2 h-2 rounded-full bg-[#ff6b35]"></span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{contact.email}</p>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-1">{contact.message}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
